import { calculateUpgradeMilestoneCount } from "@/features/hoverIdle/core/upgradeMilestones";
import type { HoverIdleSnapshot } from "@/features/hoverIdle/types";
import { compareHuge, serializeHuge, type HugeNumber } from "@/shared/numbers";
import {
  buildPlayLogAmplifierSummary,
  buildPlayLogMpSummary,
  buildPlayLogWallSummary,
  type PlayLogHovSkillSummary,
} from "./playLogBalanceSummary";
import type { PlayLogSnapshotSummary } from "./playLogTypes";

const H9G_CELL_INDEX = 8;

export interface BuildPlayLogSnapshotSummaryOptions {
  readonly hovSkillSummary?: PlayLogHovSkillSummary;
}

export function buildPlayLogSnapshotSummary(
  snapshot: HoverIdleSnapshot,
  options: BuildPlayLogSnapshotSummaryOptions = {},
): PlayLogSnapshotSummary {
  const h9gEnhancementLevel =
    snapshot.hovTargetCells[H9G_CELL_INDEX]?.enhancementLevel ?? 0;

  return {
    hov: serializeHuge(snapshot.hov),
    hovLog10: toSafeLog10(snapshot.hov),
    hovGainPerSecond: serializeHuge(snapshot.hovGainPerSecond),
    hovGainPerSecondLog10: toSafeLog10(snapshot.hovGainPerSecond),

    tim: serializeHuge(snapshot.tim),

    mHovCount: snapshot.mHov.count,
    mHovMultiplier: serializeHuge(snapshot.mHov.hgMultiplier),
    mHovMultiplierLog10: toSafeLog10(snapshot.mHov.hgMultiplier),

    nHovCount: snapshot.nHov.count,
    nHovBranchLevels: { ...snapshot.nHov.derivedBranchLevels },

    oHovCount: snapshot.oHov.count,
    hovCore: serializeHuge(snapshot.oHov.hovCore),

    hgEnhancementLevel: snapshot.hovUpgradeEnhancementLevel,
    h9gEnhancementLevel,

    hgMilestoneCount: calculateUpgradeMilestoneCount(
      snapshot.hovUpgradeEnhancementLevel,
    ),
    h9gMilestoneCount: calculateUpgradeMilestoneCount(h9gEnhancementLevel),

    ...(options.hovSkillSummary !== undefined
      ? { hovSkill: options.hovSkillSummary }
      : {}),
    amplifier: buildPlayLogAmplifierSummary(snapshot.hovAmplifier),
    wall: buildPlayLogWallSummary(snapshot.wall),
    mp: buildPlayLogMpSummary(snapshot),
  };
}

function toSafeLog10(value: HugeNumber): number | null {
  if (!value.isFinite() || compareHuge(value, 0) <= 0) {
    return null;
  }

  try {
    const result = value.log10().toNumber();
    return Number.isFinite(result) ? result : null;
  } catch {
    return null;
  }
}
